import request from "utils/request";

import { baseRequestURL, constructQueryParams } from "api/BaseAPI";
import { AibleClientFactory } from "client/aible/factory";

export function getReports(folderId, fetchAll = true, latestId) {
  let query = "&order_by=id&reverse=1&next=10";
  if (fetchAll) {
    query = "";
  } else if (latestId) {
    query += "&id=" + latestId.toString();
  }

  return request(baseRequestURL + "v1/reports/?folder_id=" + folderId + query, {
    method: "GET"
  }).then((response) => response);
}

export function loadConversations(report) {
  const chatGPTConvo = report?.metadata?.chatGPTConvo ?? {};

  if (chatGPTConvo.accountId === undefined || chatGPTConvo.conversation !== undefined) {
    return Promise.resolve(report);
  }

  const accountClient = AibleClientFactory.getInstance().getAccountClient();

  return accountClient
    .getCustomerClientFactory(chatGPTConvo.accountId)
    .then((factory) => factory.getMetadataClient().getReportConversation(report.id))
    .catch((e) => {
      console.log(e);
      return {};
    })
    .then((conversation) => {
      return {
        ...report,
        metadata: {
          ...report?.metadata,
          chatGPTConvo: {
            ...report?.metadata?.chatGPTConvo,
            conversation: conversation
          }
        }
      };
    });
}

export function saveConversations(id, metadata) {
  const chatGPTConvo = metadata?.chatGPTConvo ?? {};

  if (chatGPTConvo.accountId === undefined || chatGPTConvo.conversation === undefined) {
    return Promise.resolve(metadata);
  }

  const accountClient = AibleClientFactory.getInstance().getAccountClient();

  return accountClient
    .getCustomerClientFactory(chatGPTConvo.accountId)
    .then((factory) => factory.getMetadataClient().setReportConversation(id, chatGPTConvo.conversation))
    .catch((e) => {
      console.log(e);
      return {};
    })
    .then(() => {
      return {
        ...metadata,
        chatGPTConvo: {
          ...metadata?.chatGPTConvo,
          conversation: undefined
        }
      };
    });
}

export function loadCustomerMetadata(report) {
  const chatGPTConvo = report?.metadata?.chatGPTConvo ?? {};

  if (chatGPTConvo.accountId === undefined || chatGPTConvo.conversation !== undefined) {
    return Promise.resolve(report);
  }

  const accountClient = AibleClientFactory.getInstance().getAccountClient();

  return accountClient
    .getCustomerClientFactory(chatGPTConvo.accountId)
    .then((factory) => factory.getMetadataClient().getReportMetadata(report.id))
    .catch((e) => {
      console.log(e);
      return loadConversations(report);
    })
    .then(({ chatGPTConvo, fewShotExamples }) => {
      return {
        ...report,
        metadata: {
          ...report?.metadata,
          chatGPTConvo: {
            ...report?.metadata?.chatGPTConvo,
            ...chatGPTConvo
          },
          fewShotExamples: {
            ...report?.metadata?.fewShotExamples,
            ...fewShotExamples
          }
        }
      };
    });
}

export function saveCustomerMetadata(id, metadata) {
  const { chatGPTConvo, fewShotExamples, ...remainingMetadata } = metadata;

  const customerMetadata = {
    chatGPTConvo: chatGPTConvo ?? {},
    fewShotExamples: fewShotExamples ?? {}
  };

  if (chatGPTConvo?.accountId === undefined || chatGPTConvo?.conversation === undefined) {
    return Promise.resolve(metadata);
  }

  const accountClient = AibleClientFactory.getInstance().getAccountClient();

  return accountClient
    .getCustomerClientFactory(chatGPTConvo?.accountId)
    .then((factory) => factory.getMetadataClient().setReportMetadata(id, customerMetadata))
    .catch((e) => {
      console.log(e);
      return saveConversations(id, metadata);
    })
    .then(() => ({
      ...remainingMetadata,
      chatGPTConvo: {
        accountId: chatGPTConvo?.accountId
      }
    }));
}

export function getReport(id) {
  return request(baseRequestURL + "v1/reports/" + id, {
    method: "GET"
  })
    .then((response) => response)
    .then((report) => loadCustomerMetadata(report));
}

export function getReportByProjectId(projectId, folderId) {
  return request(baseRequestURL + "v1/reports/?project_id=" + projectId + "&folder_id=" + folderId, {
    method: "GET"
  }).then((response) => response);
}

export function getReportByDatasetId(datasetId, folderId) {
  return request(baseRequestURL + "v1/reports/?dataset_id=" + datasetId + "&folder_id=" + folderId, {
    method: "GET"
  }).then((response) => response);
}

function stripSqlKeysFromConversation(report) {
  const conversation = report?.chatGPTConvo?.conversation ?? {};

  return {
    ...report,
    chatGPTConvo: {
      ...report?.chatGPTConvo,
      conversation: Object.keys(conversation).reduce((cleaned, id) => {
        if (conversation[id].sqlQuery !== undefined) {
          cleaned[id] = {
            ...conversation[id],
            sqlLlmPrompt: "REDACTED",
            sqlQuery: "REDACTED",
            sqlQueryPrompt: "REDACTED"
          };
        } else {
          cleaned[id] = conversation[id];
        }

        return cleaned;
      }, {})
    }
  };
}

export function createReport(folderId, report, label) {
  const conversation = report?.chatGPTConvo?.conversation;

  const metadata = conversation === undefined ? report : JSON.parse(JSON.stringify(report));

  delete metadata.chatGPTConvo.conversation;

  return request(baseRequestURL + "v1/reports/", {
    method: "POST",
    body: JSON.stringify({
      folder_id: folderId,
      name: label ? label : "Some Report",
      description: report && report.description ? report.description : "",
      metadata: metadata
    })
  }).then((report) => {
    return saveCustomerMetadata(
      report.id,
      stripSqlKeysFromConversation({
        ...report?.metadata,
        chatGPTConvo: {
          ...report?.metadata?.chatGPTConvo,
          conversation: conversation
        }
      })
    ).then((update) => ({ ...report, metadata: update }));
  });
}

export function patchReport(id, report) {
  return saveCustomerMetadata(id, stripSqlKeysFromConversation(report.metadata)).then((update) =>
    request(baseRequestURL + "v1/reports/" + id, {
      method: "PATCH",
      body: JSON.stringify({
        metadata: { ...report.metadata, ...update }
      })
    }).then((response) => loadCustomerMetadata(response))
  );
}

export function moveReportFolder(id, folderId) {
  return request(baseRequestURL + "v1/reports/" + id, {
    method: "PATCH",
    body: JSON.stringify({
      folder_id: folderId
    })
  }).then((response) => response);
}

export function updateReport(id, report) {
  return saveCustomerMetadata(id, stripSqlKeysFromConversation(report)).then((report) =>
    request(baseRequestURL + "v1/reports/" + id, {
      method: "PATCH",
      body: JSON.stringify({
        metadata: { ...report }
      })
    }).then((response) => loadCustomerMetadata(response))
  );
}

export function deleteReport(id, report) {
  return request(baseRequestURL + "v1/reports/" + id, {
    method: "DELETE"
  }).then((response) => response);
}

export function addChart(reportId, datasetId, chart) {
  return request(baseRequestURL + "v1/reports/" + reportId + "/charts", {
    method: "POST",
    body: JSON.stringify({
      training_data_id: Number(datasetId),
      name: chart && chart.title ? chart.title : "Some Chart",
      description: chart && chart.description ? chart.description : "",
      data: { ...chart }
    })
  }).then((response) => response);
}

export function patchChart(chartId, chart) {
  return request(baseRequestURL + "v1/report_charts/" + chartId, {
    method: "PATCH",
    body: JSON.stringify({
      name: chart.name,
      data: { ...chart.data }
    })
  }).then((response) => response);
}

export function deleteChart(chartId) {
  return request(baseRequestURL + "v1/report_charts/" + chartId, {
    method: "DELETE"
  }).then((response) => response);
}
